import { Ref, useCallback, useImperativeHandle } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { AttachArticle } from "../../../AttachArticle/AttachArticle";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import { useResetFields } from "@coworker/apprestructured/src/task-management/services/useResetFields";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";

export type AttachArticleCreatorFragmentConfigProps = {
  field: string;
  cannotChangeItem?: boolean;
  resetFieldsOnChange: string[];
  withPriceAndCurrency?: boolean;
};

type AttachArticleCreatorFragmentProps = {
  configProps: AttachArticleCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

const NO_ITEM_NOS: ItemNoAndType[] = [];

function AttachArticleCreatorFragment({
  configProps,
  commonProps,
  ref,
}: AttachArticleCreatorFragmentProps) {
  const {
    field,
    resetFieldsOnChange,
    withPriceAndCurrency,
    cannotChangeItem = false,
  } = configProps;
  const { blockInput, task, onTaskChange, showValidation, taskType } =
    commonProps;

  // Level 1: Schema validation — does the data have the right shape?
  const itemNoAndType = validateInitialDetailsFieldValue<ItemNoAndType>(
    getByPath(task, field),
    field,
    taskType
  );
  // Level 2: Business validation — does the item actually exist?
  const { items, isFetchingItems } = useAllAttachedItems(
    isLoading(itemNoAndType)
      ? LOADING
      : itemNoAndType === null
        ? NO_ITEM_NOS
        : [itemNoAndType]
  );

  const resetFields = useResetFields(taskType);

  const handleItemChange = useCallback(
    (newItemNoAndType: ItemNoAndType) => {
      const { no, type, ...rest } = newItemNoAndType as ItemNoAndType & {
        currency?: string;
        price?: number;
      };
      const value = withPriceAndCurrency ? { no, type, ...rest } : { no, type };
      onTaskChange(setByPath(field, value, resetFields(resetFieldsOnChange)));
    },
    [
      field,
      onTaskChange,
      resetFieldsOnChange,
      resetFields,
      withPriceAndCurrency,
    ]
  );

  // Valid only if:
  // 1. Type is correct (itemNoAndType is not null)
  // 2. Data exists (item was found in the system)
  const valid =
    !isLoading(itemNoAndType) &&
    itemNoAndType !== null &&
    !isFetchingItems &&
    items[0] != null;

  useImperativeHandle(ref, () => {
    return { valid };
  });

  if (isLoading(itemNoAndType) || isFetchingItems) {
    return <LoadingCreatorFragment />;
  }

  return (
    <AttachArticle
      item={items[0] ?? null}
      setItemNoAndType={handleItemChange}
      disabled={blockInput || isFetchingItems || cannotChangeItem}
      isError={!valid && showValidation}
    />
  );
}

export default AttachArticleCreatorFragment;
